import React, { useState, useEffect } from "react";
import {
    Box, TextField, Button, Typography, Divider, Card,
    Grid
} from "@mui/material";
import { db } from "../../firebase";
import { collection, getDocs, query, where, Timestamp, orderBy, onSnapshot } from "firebase/firestore";

function Customersale() {
    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [data, setData] = useState({ trips: [], payments: [], products: [] });
    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [payModes, setPayModes] = useState([]);
    const [expenseHeads, setExpenseHeads] = useState([]);
    const [staffAttendance, setStaffAttendance] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState("");

    useEffect(() => {
        loadData();
    }, []);


    // Fetch expense heads
    useEffect(() => {
        const fetchExpenseHeads = async () => {
            const snap = await getDocs(collection(db, "expenseHeads"));
            setExpenseHeads(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        };
        fetchExpenseHeads();
    }, []);


    const loadData = async () => {

        const customerQuery = query(
            collection(db, "customers"),
            orderBy("customerName", "asc")
        );

        const unsubCustomers = onSnapshot(customerQuery, snap => {
            setCustomers(
                snap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }))
            );
        });

        const unsubPayModes = onSnapshot(collection(db, "payment_modes"), snap => {
            setPayModes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => {
            unsubCustomers();
            unsubPayModes();
        };

    };

    // Helper: Get Names from Props
    const getCustName = (id) => customers.find(c => c.id === id)?.customerName || id || "Unknown";
    const getPayModeName = (id) => payModes.find(m => m.payMode === id)?.payMode;

    const fetchAllData = async () => {
        if (!fromDate || !toDate) {
            alert("Select date range");
            return;
        }

        setLoading(true);

        try {
            const start = Timestamp.fromDate(
                new Date(fromDate + " 00:00:00")
            );

            const end = Timestamp.fromDate(
                new Date(toDate + " 23:59:59")
            );

            // QUERIES
            const qTrips = query(
                collection(db, "trips"),
                where("grossDate", ">=", start),
                where("grossDate", "<=", end),
                orderBy("grossDate", "asc")
            );

            const qPay = query(
                collection(db, "customer_history"),
                where("date", ">=", start),
                where("date", "<=", end),
                orderBy("date", "asc")
            );

            // FETCH
            const [tripSnap, paySnap] = await Promise.all([
                getDocs(qTrips),
                getDocs(qPay)
            ]);

            // =========================
            // TRIPS
            // =========================
            let trips = tripSnap.docs
                .map(doc => {
                    const tripData = {
                        id: doc.id,
                        ...doc.data()
                    };

                    const calculatedTripTotal =
                        (tripData.products || []).reduce(
                            (acc, p) =>
                                acc + (Number(p.amount) || 0),
                            0
                        );

                    return {
                        ...tripData,
                        calculatedTotal: calculatedTripTotal
                    };
                })
                .filter(trip => trip.calculatedTotal > 0);

            // CUSTOMER FILTER
            if (selectedCustomer) {
                trips = trips.filter(
                    t =>
                        t.customerId === selectedCustomer ||
                        t.customer === selectedCustomer
                );
            }

            // =========================
            // PAYMENTS
            // =========================
            let payments = paySnap.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter(pData => !pData.tripId)
                .map(pData => ({
                    ...pData,
                    displayCustomer:
                        pData.customerName ||
                        getCustName(
                            pData.customerId || pData.customer
                        ),

                    displayMode:
                        getPayModeName(
                            pData.paymentMode || pData.payMode
                        )
                }));

            // CUSTOMER FILTER
            if (selectedCustomer) {
                payments = payments.filter(
                    p =>
                        p.customerId === selectedCustomer ||
                        p.customer === selectedCustomer
                );
            }

            // =========================
            // PRODUCT SUMMARY
            // =========================
            const productMap = {};

            trips.forEach(trip => {
                (trip.products || []).forEach(p => {
                    const name = p.productName || "General";

                    if (!productMap[name]) {
                        productMap[name] = {
                            name,
                            qty: 0,
                            amount: 0,
                            count: 0
                        };
                    }

                    productMap[name].qty += Number(p.qty) || 0;
                    productMap[name].amount += Number(p.amount) || 0;
                    productMap[name].count += 1;
                });
            });

            // =========================
            // SET DATA
            // =========================
            setData({
                trips,
                payments,
                products: Object.values(productMap)
            });

        } catch (err) {
            console.error("Fetch Error:", err);
            alert("Error fetching data: " + err.message);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchAllData();
    }, [customers, payModes, selectedCustomer]); // Re-run mapping if dependencies update

    // Stylings - Increased sizes slightly for clarity
    const cellStyle = { border: '1px solid #000', padding: '3px 6px', fontSize: '0.85rem', color: '#322626' };
    const headerStyle = { ...cellStyle, fontWeight: 'bold', backgroundColor: '#eeeeee', textAlign: 'center', fontSize: '0.9rem' };
    const nestedStyle = { ...cellStyle, border: 'none', fontSize: '0.75rem', padding: '2px 4px' };

    const paymentModeList = payModes.map(m => m.payMode); // ["Cash","PhonePe","RTGS"]

    const getPaymentByMode = (trip, mode) => {
        if (!trip.payments || trip.payments.length === 0) return 0;

        return trip.payments
            .filter(p => (p.mode) === mode)
            .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    };

    // TOTAL PER PAYMENT MODE
    const getTotalByMode = (mode) => {
        return data.trips.reduce((sum, trip) => {
            if (!trip.payments) return sum;

            const modeTotal = trip.payments
                .filter(p => p.mode === mode)
                .reduce((s, p) => s + (Number(p.amount) || 0), 0);

            return sum + modeTotal;
        }, 0);
    };

    // TOTAL PAID
    const totalPaid = data.payments.reduce(
        (sum, p) => sum + (Number(p.amount) || 0),
        0
    );

    const totalSale = data.trips.reduce(
        (sum, t) => sum + (Number(t.calculatedTotal) || 0),
        0
    );

    const totalPending = totalSale - totalPaid;

    const productTotals = data.products.reduce(
        (acc, p) => {
            acc.qty += Number(p.qty || 0);
            acc.amount += Number(p.amount || 0);
            acc.count += Number(p.count || 0);
            return acc;
        },
        { qty: 0, amount: 0, count: 0 }
    );

    const getTripPaid = (trip) => {
        return data.payments
            .filter(p => p.tripId === trip.id) // ensure you store tripId in payments
            .reduce((sum, p) => sum + Number(p.amount || 0), 0);
    };

    // 1. Calculate Total Qty for all products across all trips
    const totalQty = data.trips.reduce((sum, trip) =>
        sum + (trip.products || []).reduce((pSum, p) => pSum + (Number(p.qty) || 0), 0), 0
    );

    // 2. Total Paid specifically from the Trip Sales Log (Spot/Trip payments)
    const totalTripPaid = data.trips.reduce((sum, t) => sum + (Number(t.paidAmount) || 0), 0);

    // 3. Collection History Total (Purely from customer_history/backdate entries)
    const totalCollectionHistory = data.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    const bolderTotals = (data.bolderData || []).reduce((acc, trip) => {
        const tons = Number(trip.netWeight || 0) / 1000;
        const amount = tons * 92;

        acc.count += 1;
        acc.totalTons += tons;
        acc.totalAmount += amount;
        return acc;
    }, { count: 0, totalTons: 0, totalAmount: 0 });

    const purchaseSummary = (data.purchases || []).reduce((acc, p) => {
        acc.total += Number(p.calculatedTotal || 0);
        acc.count += 1;
        return acc;
    }, { total: 0, count: 0 });


    // ✅ INCOME
    const totalCollection = data.payments.reduce(
        (sum, p) => sum + (Number(p.amount) || 0),
        0
    );

    const totalSaleAmount = data.trips.reduce(
        (sum, t) => sum + (Number(t.totalAmount) || 0),
        0
    );

    const totalIncome = totalSaleAmount + totalCollection;


    // ✅ EXPENSES
    const totalBolderExpense = bolderTotals.totalAmount || 0;
    console.log(totalBolderExpense);


    const totalPurchaseExpense = purchaseSummary.total || 0;

    const totalOtherExpense = data.expenses?.grandTotal || 0;

    const totalLightBill = 56666; // static (you can make dynamic later)

    const totalStaffSalary = data.totalSalaryExpense || 0;
    const royaltyRate = 670;

    const totalRoyaltyExpense = totalQty * royaltyRate;

    const totalExpense =
        totalBolderExpense +
        totalPurchaseExpense +
        totalOtherExpense +
        totalLightBill +
        totalRoyaltyExpense +   // ✅ added
        totalStaffSalary;

    const totalFixedOtherExpense = totalLightBill + totalRoyaltyExpense;

    // ✅ PROFIT / LOSS
    const netProfit = totalIncome - totalExpense;




    return (
        <Box p={1} sx={{ bgcolor: "#fff" }}>
            {/* Filters */}
            <Card sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', borderRadius: '8px' }} className="no-print">
                <TextField
                    select
                    size="small"
                    label="Customer"
                    value={selectedCustomer}
                    onChange={(e) => setSelectedCustomer(e.target.value)}
                    SelectProps={{ native: true }}
                    sx={{ minWidth: 250 }}
                >
                    <option value="">All Customers</option>

                    {customers.map((cust) => (
                        <option key={cust.id} value={cust.id}>
                            {cust.customerName}
                        </option>
                    ))}
                </TextField>
                <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
                <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={toDate} onChange={e => setToDate(e.target.value)} />
                <Button variant="contained" onClick={fetchAllData} disabled={loading} sx={{ minWidth: '120px' }}>{loading ? "Fetching..." : "Generate"}</Button>
                <Button variant="outlined" onClick={() => window.print()}>Print PDF</Button>
            </Card>

            <Grid className="printable-area p-2">
                <Box sx={{ border: '2px solid #000' }}>
                    <Box textAlign="center" mb={2}>
                        {/* <Typography variant="h5" fontWeight="bold">TEJAS STONE CRUSHER</Typography>
                        <Typography variant="body2">Varuda Road, Dist. Dharashiv, Maharashtra</Typography> */}
                        <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold', letterSpacing: 1 }}>
                            MASTER SALE & COLLECTION REPORT ({fromDate} TO {toDate})
                        </Typography>
                        <Divider sx={{ my: 2, bgcolor: '#000', height: '2px' }} />
                    </Box>

                    {/* SECTION 1: TRIPS */}
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1, color: "green" }}> &nbsp;I. TRIP SALES LOG</Typography>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '5px' }}>
                        <thead>
                            <tr>
                                <th style={{ ...headerStyle, width: '20px' }}>Sr.</th>
                                <th style={{ ...headerStyle, width: '60px' }}>Tkt</th>
                                <th style={{ ...headerStyle, width: '140px' }}>Date</th>
                                <th style={{ ...headerStyle, width: '40px' }}>Chalan No</th>
                                <th style={{ ...headerStyle, width: '150px' }}>Customer</th>
                                <th style={{ ...headerStyle, width: '150px' }}>Mobile</th>
                                <th style={{ ...headerStyle, width: '100px' }}>Vehicle</th>

                                {/* SEPARATED PRODUCT COLUMNS */}
                                <th style={{ ...headerStyle, width: '180px', textAlign: 'left' }}>Product Item</th>
                                <th style={{ ...headerStyle, width: '60px' }}>Qty</th>

                                <th style={{ ...headerStyle, width: '100px' }}>Total</th>

                                {/* Dynamic Payment Modes */}
                                {paymentModeList.map((mode, i) => (
                                    <th key={i} style={{ ...headerStyle, width: '90px' }}>{mode}</th>
                                ))}

                                <th style={{ ...headerStyle, width: '90px' }}>Paid</th>
                                <th style={{ ...headerStyle, width: '90px' }}>Pending</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.trips.map((row, index) => (
                                <tr key={row.id}>
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>{index + 1}</td>
                                    <td style={{ ...cellStyle }}><strong>{row.ticketNo}</strong></td>
                                    <td style={{ ...cellStyle }}>
                                        {row.grossDate ? (
                                            row.grossDate.toDate().toLocaleString('en-GB', {
                                                day: '2-digit', month: '2-digit', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit', hour12: false
                                            }).replace(/\//g, '/')
                                        ) : "-"}
                                    </td>
                                    <td style={{ ...cellStyle }}><strong>{row.challan}</strong></td>
                                    <td style={{ ...cellStyle }}><strong>{row.customerName}</strong></td>
                                    <td style={{ ...cellStyle }}><strong>{row.mobile}</strong></td>
                                    <td style={{ ...cellStyle, fontSize: '0.75rem' }}>{row.vehicleNo}</td>

                                    {/* SEPARATED PRODUCT NAME COLUMN */}
                                    <td style={{ ...cellStyle, padding: 0 }}>
                                        {(row.products || []).map((p, i) => (
                                            <div key={i} style={{
                                                padding: '4px 8px',
                                                borderBottom: i === row.products.length - 1 ? 'none' : '1px solid #ddd',
                                                minHeight: '20px'
                                            }}>
                                                {p.productName}
                                            </div>
                                        ))}
                                    </td>

                                    {/* SEPARATED QTY COLUMN */}
                                    <td style={{ ...cellStyle, padding: 0, textAlign: 'center' }}>
                                        {(row.products || []).map((p, i) => (
                                            <div key={i} style={{
                                                padding: '4px 8px',
                                                borderBottom: i === row.products.length - 1 ? 'none' : '1px solid #ddd',
                                                fontWeight: '500',
                                                minHeight: '20px'
                                            }}>
                                                {p.qty}
                                            </div>
                                        ))}
                                    </td>

                                    <td style={{ ...cellStyle, textAlign: "right" }}>
                                        ₹{Number(row.totalAmount || 0).toLocaleString()}
                                    </td>

                                    {paymentModeList.map((mode, i) => (
                                        <td key={i} style={{ ...cellStyle, textAlign: "right" }}>
                                            ₹{getPaymentByMode(row, mode)}
                                        </td>
                                    ))}

                                    <td style={{ ...cellStyle, textAlign: "right", color: 'green' }}>
                                        ₹{Number(row.paidAmount || 0).toLocaleString()}
                                    </td>

                                    <td style={{ ...cellStyle, textAlign: "right", color: "red", fontWeight: "bold" }}>
                                        ₹{Number(row.remainingAmount || 0).toLocaleString()}
                                    </td>
                                </tr>
                            ))}

                            <tr style={{ backgroundColor: '#eeeeee', fontWeight: 'bold' }}>
                                {/* Span 6 covers: Sr, Date, Ticket, Customer, Vehicle, Product Name */}
                                <td colSpan={8} style={{ ...cellStyle, textAlign: 'right', fontSize: '1rem' }}>
                                    TOTAL SUMMARY
                                </td>

                                {/* Display Total Brass/Quantity */}
                                <td style={{ ...cellStyle, textAlign: 'center' }}>
                                    {totalQty.toFixed(2)}
                                </td>

                                <td style={{ ...cellStyle, textAlign: 'right' }}>
                                    ₹{data.trips.reduce((s, i) => s + (Number(i.totalAmount) || 0), 0).toLocaleString()}
                                </td>

                                {paymentModeList.map((mode, i) => (
                                    <td key={i} style={{ ...cellStyle, textAlign: 'right' }}>
                                        ₹{getTotalByMode(mode).toLocaleString()}
                                    </td>
                                ))}

                                {/* Trip-specific Paid Total */}
                                <td style={{ ...cellStyle, textAlign: 'right', color: 'green' }}>
                                    ₹{totalTripPaid.toLocaleString()}
                                </td>

                                <td style={{ ...cellStyle, textAlign: 'right', color: 'red' }}>
                                    ₹{(totalSale - totalTripPaid).toLocaleString()}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    {/* SECTION 2: PAYMENTS */}
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1, color: "green" }}> &nbsp;II. CUSTOMER COLLECTION HISTORY</Typography>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '5px' }}>
                        <thead>
                            <tr>
                                <th style={headerStyle}>Sr.</th>
                                <th style={headerStyle}>Payment Date</th>
                                <th style={headerStyle}>Customer Name</th>
                                <th style={headerStyle}>Payment Mode</th>
                                <th style={headerStyle}>Paid Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.payments.map((row, index) => (
                                <tr key={row.id}>
                                    <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>{index + 1}</td>
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>
                                        {row.date ? (
                                            (() => {
                                                const d = row.date.toDate();
                                                const day = String(d.getDate()).padStart(2, '0');
                                                const month = String(d.getMonth() + 1).padStart(2, '0');
                                                const year = d.getFullYear();
                                                return `${day}-${month}-${year}`; // Change separators here if needed
                                            })()
                                        ) : "-"}
                                    </td>
                                    <td style={cellStyle}>{row.displayCustomer}</td>
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>{row.displayMode}</td>
                                    <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                        ₹{(Number(row.amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </td>
                                </tr>
                            ))}
                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                <td colSpan={4} style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '1rem' }}>TOTAL COLLECTION</td>
                                <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold', fontSize: '1rem' }}>
                                    ₹{data.payments.reduce((s, i) => s + (Number(i.amount) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* SECTION 3: PRODUCT SUMMARY */}
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 1, color: "green" }}> &nbsp;III. PRODUCT WISE SUMMARY</Typography>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={headerStyle}>Sr.</th>
                                <th style={headerStyle}>Product Name</th>
                                <th style={headerStyle}>Trip Count</th>
                                <th style={headerStyle}>Total Quantity</th>
                                <th style={headerStyle}>Total Sales Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.products.map((row, index) => (
                                <tr key={index}>
                                    <td style={{ ...cellStyle, textAlign: 'center', width: '40px' }}>{index + 1}</td>
                                    <td style={cellStyle}>{row.name}</td>
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>{row.count}</td>
                                    <td style={{ ...cellStyle, textAlign: 'right' }}>{row.qty.toFixed(2)}</td>
                                    <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                        ₹{row.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tr style={{ backgroundColor: '#f0f0f0' }}>
                            <td style={{ ...cellStyle }}></td>

                            <td style={{ ...cellStyle, fontWeight: 'bold' }}>
                                TOTAL
                            </td>

                            <td style={{ ...cellStyle, textAlign: 'center', fontWeight: 'bold' }}>
                                {productTotals.count}
                            </td>

                            <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                {productTotals.qty.toFixed(2)}
                            </td>

                            <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 'bold' }}>
                                ₹{productTotals.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                        </tr>
                    </table>




                </Box>
            </Grid>

            <style>{`
        @media print {
          body * { visibility: hidden; }
          .printable-area, .printable-area * { visibility: visible; }
          .printable-area { 
            position: absolute; 
            left: 0; 
            top: 0; 
            width: 100%; 
            border: none !important; 
          }
          .no-print { display: none !important; }
          @page { size: A4; margin: 10mm;size: landscape; }
        }
      `}</style>
        </Box>
    );
}

export default Customersale;